import * as React from "react";
import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  Container,
  Divider,
  Dialog,
  DialogTitle,
  DialogActions,
  CircularProgress,
  LinearProgress,
  Alert,
  Fade,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Login as LoginIcon,
  Apple as AppleIcon,
  Computer as WindowsIcon,
  Memory as LinuxIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";

import DDLogo from "../assets/DD_LOGO_TRANSPARENT_S.png";

// Services
import { authApi, downloadService, firebaseService } from "../services";
import onboardingService from "../services/onboardingService";
import { isSafari } from "@firebase/util";
import { isElectron } from "../util/ElectronDetection";
import { useProductContext } from "../hooks/useProductContext";
import { useAuthStore } from "../stores/zustand";

// Components
import AuthPageWrapper from "../components/AuthPageWrapper";
import ProductSelectionPopup from "../components/ProductSelectionPopup";
import { prefetchAllData } from "../hooks/useScheduler";
import { useQueryClient } from "@tanstack/react-query";

/**
 * SignIn Component (Landing Page + Authentication)
 *
 * A landing page similar to WhatsApp with:
 * - Platform detection and download buttons
 * - Sign in functionality
 * - Responsive design
 *
 * @returns {JSX.Element} Landing page with download and authentication options
 */
export default function SignIn() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const queryClient = useQueryClient();
  const productContext = useProductContext();
  const setAuthData = useAuthStore((state) => state.setAuthData); // For setting auth data in Zustand store

  // Landing page state
  const [isRunningInElectron, setIsRunningInElectron] = useState(false);
  // Temporarily force showing sign in form by default
  const [showSignInForm, setShowSignInForm] = useState(true);
  const [platformInfo, setPlatformInfo] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const [showMacOSInstructions, setShowMacOSInstructions] = useState(false);

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");

  // Loading and dialog states
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [forgotPasswordDialogOpen, setForgotPasswordDialogOpen] =
    useState(false);
  const [openEmailVerification, setOpenEmailVerification] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);

  // Error state
  const [error, setError] = useState("");
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState("");

  // State to track the previous location that redirected to login
  const [previousLocation, setPreviousLocation] = useState(null);

  // Product selection popup state
  const [showProductPopup, setShowProductPopup] = useState(false);
  const [authResponseForPopup, setAuthResponseForPopup] = useState(null);
  const [productChangeLoading, setProductChangeLoading] = useState(false);

  useEffect(() => {
    const prevLocation = location.state?.from;
    if (prevLocation) {
      setPreviousLocation(prevLocation);
    }
  }, [location.state]);

  /**
   * Check if running in Electron and set initial state
   */
  useEffect(() => {
    const runningInElectron = isElectron();
    setIsRunningInElectron(runningInElectron);

    // If running in Electron, skip landing page and show sign-in form directly
    if (runningInElectron) {
      setShowSignInForm(true);
    }
  }, []);

  /**
   * Initialize platform detection on component mount (only if not in Electron)
   */
  useEffect(() => {
    // Only detect platform if we're not in Electron (since we won't show download options)
    if (!isRunningInElectron) {
      try {
        const detectedPlatform = downloadService.detectPlatform();
        setPlatformInfo(detectedPlatform);
      } catch (error) {
        console.error("Platform detection failed:", error);
      }
    }
  }, [isRunningInElectron]);

  /**
   * Redirect authenticated users away from login page
   */
  useEffect(() => {
    const { isAuthenticated, isEmailVerified } = useAuthStore.getState();
    if (isAuthenticated() && isEmailVerified) {
      navigate("/home", { replace: true }); // Redirect to home if already authenticated
    }
  }, [navigate]);

  /**
   * Listen for language changes from Electron menu
   */
  useEffect(() => {
    if (window.electronAPI?.language) {
      const cleanup = window.electronAPI.language.onChange((newLanguage) => {
        i18n.changeLanguage(newLanguage);
      });

      return cleanup; // Cleanup on unmount
    }
  }, [i18n]);

  /**
   * Validates email format
   */
  const validateEmail = (email) => {
    const re = /\S+@\S+\.\S+/;
    return re.test(email);
  };

  /**
   * Handles app download
   */
  const handleDownload = async (customPlatform = null) => {
    setDownloadLoading(true);
    setDownloadError("");

    try {
      const result = await downloadService.downloadInstaller(customPlatform);

      // Show macOS security instructions for macOS downloads
      if (
        result.platform &&
        result.platform.downloadKey &&
        result.platform.downloadKey.startsWith("darwin-")
      ) {
        setTimeout(() => {
          setShowMacOSInstructions(true);
        }, 1000); // Small delay to let download start
      }
    } catch (error) {
      setDownloadError(t("downloadFailed"));
    } finally {
      setDownloadLoading(false);
    }
  };

  /**
   * Gets platform-specific download button text and icon
   */
  const getDownloadButtonInfo = () => {
    if (!platformInfo || !platformInfo.downloadKey) {
      return {
        text: t("downloadApp"),
        icon: <DownloadIcon />,
        variant: "contained",
      };
    }

    const iconMap = {
      "darwin-arm64": <AppleIcon />,
      "darwin-x64": <AppleIcon />,
      "win32-x64": <WindowsIcon />,
      "linux-x64": <LinuxIcon />,
    };

    const textMap = {
      "darwin-arm64": t("downloadForMacOS"),
      "darwin-x64": t("downloadForMacOS"),
      "win32-x64": t("downloadForWindows"),
      "linux-x64": t("downloadForLinux"),
    };

    return {
      text: textMap[platformInfo.downloadKey] || t("downloadApp"),
      icon: iconMap[platformInfo.downloadKey] || <DownloadIcon />,
      variant: "contained",
    };
  };
  const getTimezoneOffset = () => {
    const offset = new Date().getTimezoneOffset() / -60;
    return offset >= 0
      ? `+${String(Math.abs(offset)).padStart(2, "0")}`
      : `-${String(Math.abs(offset)).padStart(2, "0")}`;
  };

  const completeLoginFlow = async (authResponse) => {
    const { auth } = authResponse;
    const timezoneOffset = getTimezoneOffset();
    const currentLanguage = auth.preferredLanguage || "de";
    const userEmail = auth.email || "";
    const currentProduct = auth.current_product || null;
    const onboarding_completed = auth.onboarding_completed || false;

    try {
      await prefetchAllData(
        queryClient,
        timezoneOffset,
        currentLanguage,
        userEmail,
        currentProduct,
        onboarding_completed,
      );
    } catch (error) {
      console.error("[completeLoginFlow] Prefetch failed:", error);
    }

    firebaseService.fetchAndRegisterToken(() => {});

    if (isSafari() && window.PushAlertCo?.subs_id) {
      firebaseService
        .registerSafariToken(window.PushAlertCo.subs_id)
        .catch(() => {});
    } else if (isSafari()) {
      window.pushalertbyiw = window.pushalertbyiw || [];
      window.pushalertbyiw.push([
        "onReady",
        () => {
          if (window.PushAlertCo?.subs_id) {
            firebaseService
              .registerSafariToken(window.PushAlertCo.subs_id)
              .catch(() => {});
          }
        },
      ]);
    }
    proceedWithAuthentication(authResponse);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setEmailError("");

    if (!validateEmail(email)) {
      setEmailError(t("invalidEmail"));
      return;
    }

    if (!password) {
      setError(t("passwordRequired"));
      return;
    }

    setLoading(true);

    try {
      const response = await authApi.authenticate(email, password);

      if (response.auth) {
        setAuthData(response);

        // Sync i18n with Zustand language
        const userLanguage = response.auth.preferredLanguage || "de";
        if (i18n.language !== userLanguage) {
          i18n.changeLanguage(userLanguage);
        }

        if (
          response.status === "SUCCESS" &&
          response.subscribed_products?.length > 1
        ) {
          setAuthResponseForPopup(response);
          setShowProductPopup(true);
          setLoading(false);
          return;
        }

        await completeLoginFlow(response);
      } else {
        setError(t("authenticationFailed"));
      }
    } catch (error) {
      console.error("Authentication error:", error);
      setLoading(false);

      if (error.response?.status === 401) {
        setError(t("invalidCredentials"));
      } else if (error.response?.status >= 500) {
        setError(t("serverError"));
      } else {
        setError(t("authError"));
      }

      setDialogOpen(true);
    } finally {
      if (!showProductPopup) setLoading(false);
    }
  };

  const handleProductSelection = async (selectedProduct) => {
    if (!authResponseForPopup) return;

    const currentProduct = authResponseForPopup.auth?.current_product;

    // Same product -> just continue, no loading needed
    if (selectedProduct === currentProduct) {
      setProductChangeLoading(true); // Show loading in popup
      await completeLoginFlow(authResponseForPopup);
      setProductChangeLoading(false);
      setShowProductPopup(false);
      setAuthResponseForPopup(null);
      return;
    }

    // Different product  -> switch + full prefetch
    setProductChangeLoading(true);

    try {
      const changeResponse = await authApi.changeProduct(selectedProduct);

      if (!changeResponse) {
        throw new Error("Product change failed - no response");
      }

      // Update auth store with fresh product data
      setAuthData({
        auth: {
          name: authResponseForPopup.auth.name,
          email: authResponseForPopup.auth.email,
          isPaid: changeResponse.isPaid,
          isEmailVerified: changeResponse.isEmailVerified,
          current_product: selectedProduct,
          preferredLanguage:
            authResponseForPopup.auth.preferredLanguage || "de",
          onboarding_completed: changeResponse.onboarding_completed || false,
        },
        subscribed_products: authResponseForPopup.subscribed_products || [],
      });

      const finalAuthResponse = {
        status: "SUCCESS",
        auth: {
          name: authResponseForPopup.auth.name,
          email: authResponseForPopup.auth.email,
          isPaid: changeResponse.isPaid,
          isEmailVerified: changeResponse.isEmailVerified,
          current_product: selectedProduct,
          preferredLanguage:
            authResponseForPopup.auth.preferredLanguage || "de",
          onboarding_completed: changeResponse.onboarding_completed || false,
        },
      };

      await completeLoginFlow(finalAuthResponse);

      // Only now close everything
      setProductChangeLoading(false);
      setShowProductPopup(false);
      setAuthResponseForPopup(null);
    } catch (error) {
      console.error("Product change failed:", error);
      let errorMessage = "Failed to switch product";
      if (error.response?.status === 404) {
        errorMessage =
          "Product change endpoint not found. Please contact support.";
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      setError(errorMessage);
      setProductChangeLoading(false);
    }
  };
  /**
   * Handles forgot password submission
   */
  const handleForgotPasswordSubmit = async () => {
    setForgotPasswordError("");
    setForgotPasswordSuccess("");

    if (!validateEmail(forgotPasswordEmail)) {
      setForgotPasswordError(t("invalidEmail"));
      return;
    }

    setForgotPasswordLoading(true);

    try {
      const result = await authApi.sendPasswordReset(forgotPasswordEmail);
      const status = result?.resetPasswordEmailStatus;

      if (status === "SUCCESS") {
        setForgotPasswordSuccess(t("resetEmailSent"));
      } else {
        setForgotPasswordError(t("passwordResetUnexpectedError"));
      }
    } catch (error) {
      console.error("Password reset failed:", error);
      const status = error.response?.data?.resetPasswordEmailStatus;
      if (status === "NO_USER_FOUND") {
        setForgotPasswordError(t("resetNoUserFound"));
      } else if (status === "EMAIL_EMPTY") {
        setForgotPasswordError(t("resetNoEmail"));
      } else if (status === "LINK_ALREADY_SENT") {
        setForgotPasswordError(t("resetLinkAlreadySent"));
      } else {
        setForgotPasswordError(t("passwordResetUnexpectedError"));
      }
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  /**
   * Dialog close handlers
   */
  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleForgotPasswordDialogClose = () => {
    setForgotPasswordDialogOpen(false);
    setForgotPasswordEmail("");
    setForgotPasswordError("");
    setForgotPasswordSuccess("");
  };

  const handleEmailVerificationClose = () => {
    setOpenEmailVerification(false);
    navigate("/");
  };

  /**
   * Proceeds with authentication flow using the provided response
   */
  const proceedWithAuthentication = (response) => {
    const { isPaid, isEmailVerified, current_product } = response.auth;
    // const firstLogin = response.first_login || false;

    // if (current_product === "physio_user" && isEmailVerified && firstLogin) {
    //   navigate("/confirm-new-password");
    //   return;
    // }

    if (current_product === "physio_user" && isEmailVerified) {
      navigate("/home/library");
      return;
    }

    if (current_product === "physio_therapist" && isEmailVerified && isPaid) {
      navigate("/physio-dashboard");
    } else if (current_product === "physio_therapist" && !isEmailVerified) {
      setOpenEmailVerification(true);
    } else {
      if (isPaid && isEmailVerified) {
        // Check if user needs onboarding
        const authData = { isPaid, isEmailVerified, current_product, email };
        if (onboardingService.needsOnboarding(authData)) {
          navigate("/onboarding-exercises", { state: { email } });
        } else {
          // Navigate to previous location or home
          const destination = previousLocation || "/home";
          navigate(destination);
        }
      } else if (!isEmailVerified) {
        setOpenEmailVerification(true);
      } else if (isEmailVerified && !isPaid) {
        navigate("/checkout", { state: { email } });
      }
    }
  };
  /**
   * Copyright component
   */
  function Copyright(props) {
    return (
      <Typography
        variant="body2"
        color="#063bd0"
        marginTop={"8px"}
        align="center"
        {...props}
      >
        {t("copyright")} ©
        <Link color="inherit" href={process.env.REACT_APP_FIREBASE_BASE_URL}>
          &nbsp;Desk Dynamics
        </Link>{" "}
        {new Date().getFullYear()}
        {"."}
      </Typography>
    );
  }

  const downloadButtonInfo = getDownloadButtonInfo();

  return (
    <AuthPageWrapper isMobile={isMobile}>
      <Container component="main" maxWidth="lg" sx={{ height: "100vh" }}>
        <CssBaseline />

        {/* Login Loading Dialog */}
        <Dialog
          open={loading}
          PaperProps={{
            sx: {
              borderRadius: 3,
              px: 4,
              py: 3,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 3,
              minWidth: 260,
            },
          }}
          slotProps={{
            backdrop: { sx: { backdropFilter: "blur(4px)" } },
          }}
        >
          <Box
            component="img"
            src={DDLogo}
            alt="Loading"
            sx={{
              width: 52,
              height: 52,
              animation: "spin 1.2s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
          <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
            {t("loggingIn")}
          </Typography>
        </Dialog>

        {/* Email Verification Dialog */}
        <Dialog
          open={openEmailVerification}
          onClose={handleEmailVerificationClose}
        >
          <DialogTitle>{t("weSentAnEmail")}</DialogTitle>
          <DialogActions>
            <Button onClick={handleEmailVerificationClose} color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* macOS Security Instructions Dialog */}
        <Dialog
          open={showMacOSInstructions}
          onClose={() => setShowMacOSInstructions(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <span>🍎</span>
            {t("macosSecurityNotice")}
          </DialogTitle>
          <Box sx={{ p: 3 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t("downloadComplete")}
              </Typography>
              <Typography variant="body2">
                {t("macosSecurityInstructions")}
              </Typography>
            </Alert>

            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t("macosStep1")}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t("macosStep2")}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t("macosStep3")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("macosAlternative")}
              </Typography>
            </Box>
          </Box>
          <DialogActions>
            <Button
              onClick={() => setShowMacOSInstructions(false)}
              variant="contained"
              color="primary"
            >
              {t("okay")}
            </Button>
          </DialogActions>
        </Dialog>

        <Grid container sx={{ height: "100%", width: "100%" }}>
          {/* Left side - corporate is default, preventive_course swaps text */}
          <Grid
            item
            xs={12}
            sm={6}
            sx={{
              display: { xs: "none", sm: "flex" },
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              p: 8,
            }}
          >
            <Typography variant="h3" gutterBottom sx={{ color: "#fff" }}>
              {productContext === "preventive_course"
                ? t("hiAndWelcomePreventive")
                : productContext === "corporate"
                  ? t("hiAndWelcome")
                  : productContext.includes("physio")
                    ? t("hiAndWelcomePhysio")
                    : t("hiAndWelcome")}{" "}
              <b>
                {productContext === "preventive_course"
                  ? t("product_namePreventive")
                  : productContext === "corporate"
                    ? t("product_name")
                    : productContext.includes("physio")
                      ? t("product_namePhysio")
                      : t("product_name")}{" "}
              </b>
            </Typography>

            <Typography variant="subtitle1" sx={{ color: "#fff" }}>
              <b>
                {productContext === "preventive_course"
                  ? t("authSubtitlePreventive")
                  : productContext.includes("physio")
                    ? t("authSubtitlePhysio")
                    : t("authSubtitle")}
              </b>
            </Typography>

            <Typography sx={{ color: "#fff", mt: 3, alignSelf: "flex-start" }}>
              {productContext === "preventive_course"
                ? t("authDescriptionPreventive")
                : productContext === "physio_therapist"
                  ? t("authDescriptionPhysio")
                  : productContext === "physio_user"
                    ? t("authDescriptionPhysioUser")
                    : t("authDescription")}
            </Typography>

            {!showSignInForm && !isRunningInElectron && (
              <Typography
                sx={{ color: "#fff", mt: 3, alignSelf: "flex-start" }}
              >
                {productContext === "preventive_course"
                  ? t("desktopAppBenefitsPreventive")
                  : productContext.includes("physio")
                    ? t("hiAndWelcomePhysio")
                    : t("desktopAppBenefits")}
              </Typography>
            )}

            {(showSignInForm || isRunningInElectron) && (
              <Typography
                sx={{ color: "#fff", mt: 3, alignSelf: "flex-start" }}
              >
                {t("tryOut")}
              </Typography>
            )}
          </Grid>

          {/* Right side - Landing page or Sign in form */}
          <Grid
            item
            xs={12}
            sm={6}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: isMobile ? 1 : 8,
            }}
          >
            <div
              style={{ width: "100%", padding: "36px" }}
              className={
                isMobile ? "white-background-mobile" : "white-background"
              }
            >
              {!showSignInForm && !isRunningInElectron ? (
                /* Landing Page Content */
                <Fade
                  in={!showSignInForm && !isRunningInElectron}
                  timeout={500}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Typography
                      component="h1"
                      variant="h5"
                      sx={{
                        color: "#063bd0",
                        alignSelf: "flex-start",
                        mb: 3,
                      }}
                    >
                      {t("getDesktopApp")}
                    </Typography>

                    {/* Download Error Alert */}
                    {downloadError && (
                      <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
                        {downloadError}
                      </Alert>
                    )}

                    {/* Primary Download Button */}
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={
                        downloadLoading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          downloadButtonInfo.icon
                        )
                      }
                      onClick={() => handleDownload()}
                      disabled={downloadLoading}
                      sx={{
                        width: "100%",
                        mb: 2,
                        py: 1.5,
                        fontSize: "1.1rem",
                        fontWeight: "bold",
                        boxShadow: "none",
                        "&:hover": {
                          boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                        },
                      }}
                    >
                      {downloadLoading
                        ? t("downloading")
                        : downloadButtonInfo.text}
                    </Button>

                    {/* Platform Info */}
                    {platformInfo && platformInfo.displayName && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#fff",
                          mb: 2,
                          alignSelf: "center",
                        }}
                      >
                        {t("detectedPlatform", {
                          platform: platformInfo.displayName,
                        })}
                      </Typography>
                    )}

                    <Divider sx={{ width: "100%", mb: 3 }} />

                    {/* Web App Option */}
                    <Typography
                      sx={{
                        color: "#063bd0",
                        mb: 2,
                        textAlign: "center",
                      }}
                    >
                      {t("alreadyHaveApp")}
                    </Typography>

                    <Button
                      variant="outlined"
                      startIcon={<LoginIcon />}
                      onClick={() => setShowSignInForm(true)}
                      sx={{
                        width: "100%",
                        py: 1.5,
                        fontSize: "1rem",
                        color: "#fff",
                        border: "1px solid #fff",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: "#05b67f",
                          color: "#fff",
                          border: "1px solid #05b67f",
                          boxShadow: "none",
                        },
                      }}
                    >
                      {t("loginToWebApp")}
                    </Button>

                    <Copyright sx={{ mt: 4 }} />
                  </Box>
                </Fade>
              ) : (
                /* Sign In Form */
                <Fade in={showSignInForm || isRunningInElectron} timeout={500}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    {/* Back to Landing Button temporarily hidden while downloads are disabled */}

                    <Typography
                      component="h1"
                      variant="h6"
                      style={{ color: "#063bd0", alignSelf: "flex-start" }}
                    >
                      {t("signIn")}
                    </Typography>

                    <form onSubmit={handleSubmit} style={{ width: "100%" }}>
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label={t("emailAddress")}
                        name="email"
                        autoComplete="email"
                        // autoFocus
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          setEmailError("");
                        }}
                        error={!!emailError}
                        helperText={emailError}
                        disabled={loading}
                      />
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label={t("password")}
                        type="password"
                        id="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                        }}
                        disabled={loading}
                      />
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        sx={{
                          width: "50%",
                          mt: 3,
                          mb: 2,
                          float: "right",
                          boxShadow: "none",
                          "&:hover": {
                            boxShadow: "none",
                          },
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          t("signIn")
                        )}
                      </Button>
                    </form>

                    {/* Error Alert */}
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => setForgotPasswordDialogOpen(true)}
                      sx={{ mt: 2 }}
                      disabled={loading}
                    >
                      {t("forgotPassword")}
                    </Link>

                    {/* Registration Section */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        maxWidth: 400,
                        width: "100%",
                        mt: 4,
                      }}
                    >
                      <Divider
                        sx={{
                          width: "100%",
                          height: "2px",
                          bgcolor: "divider",
                        }}
                      />
                      <Typography
                        sx={{
                          color: "#063bd0",
                          mt: 2,
                          alignSelf: "flex-start",
                          marginTop: "28px",
                        }}
                      >
                        {t("dontAccount")}
                      </Typography>
                      <Typography
                        sx={{
                          color: "#063bd0",
                          alignSelf: "flex-start",
                          marginBottom: "8px",
                        }}
                      >
                        {t("registerHere")}
                      </Typography>
                      <Button
                        onClick={() => {
                          // Navigate to appropriate register route based on product context
                          if (productContext === "corporate") {
                            navigate("/register-corporate", {
                              state: { productType: "corporate" },
                            });
                          } else if (productContext === "preventive_course") {
                            navigate("/register-course", {
                              state: { productType: "preventive_course" },
                            });
                          } else if (productContext === "physio_therapist") {
                            navigate("/register-physio");
                          } else if (productContext === "physio_user") {
                            navigate("/register-physio-user", {
                              state: { productType: "physio_user" },
                            });
                          } else {
                            navigate("/register");
                          }
                        }}
                        variant="outlined"
                        disabled={loading}
                        sx={{
                          width: "50%",
                          mt: 2,
                          mb: 2,
                          backgroundColor: "transparent",
                          color: "#063bd0",
                          border: "1px solid #063bd0",
                          alignSelf: "flex-end",
                          boxShadow: "none",
                          "&:hover": {
                            color: "#ffffff",
                            boxShadow: "none",
                            border: "1px solid #05b67f",
                            bgcolor: "transparent",
                          },
                        }}
                      >
                        {t("register")}
                      </Button>
                    </Box>

                    <Copyright />
                  </Box>
                </Fade>
              )}
            </div>
          </Grid>

          {/* Error Dialog */}
          <Dialog open={dialogOpen} onClose={handleDialogClose} PaperProps={{ sx: { borderRadius: "15px", minWidth: 320 } }}>
            <DialogTitle sx={{ px: 4, pt: 3, pb: 1 }}>{t("authError")}</DialogTitle>
            <Box sx={{ px: 4, pb: 2 }}>
              <Typography>{error || t("anAuthErrorOccurred")}</Typography>
            </Box>
            <DialogActions sx={{ px: 4, pb: 3 }}>
              <Button onClick={handleDialogClose}>{t("close")}</Button>
            </DialogActions>
          </Dialog>

          {/* Forgot Password Dialog */}
          <Dialog
            open={forgotPasswordDialogOpen}
            onClose={handleForgotPasswordDialogClose}
            PaperProps={{ sx: { borderRadius: "15px", minWidth: 320 } }}
          >
            {forgotPasswordLoading && <LinearProgress />}
            <DialogTitle sx={{ px: 4, pt: 3, pb: 1 }}>{t("forgotPassword")}</DialogTitle>
            <Box sx={{ px: 4, pb: 2 }}>
              <Typography sx={{ mb: 2 }}>{t("pleaseEnterEmail")}</Typography>

              {forgotPasswordError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {forgotPasswordError}
                </Alert>
              )}

              {forgotPasswordSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {forgotPasswordSuccess}
                </Alert>
              )}

              <TextField
                // autoFocus
                margin="dense"
                id="forgot-password-email"
                label={t("emailAddress")}
                type="email"
                fullWidth
                variant="standard"
                value={forgotPasswordEmail}
                onChange={(e) => {
                  setForgotPasswordEmail(e.target.value);
                  setForgotPasswordError(e.target.value && !validateEmail(e.target.value) ? t("invalidEmail") : "");
                }}
                error={!!forgotPasswordError && forgotPasswordError === t("invalidEmail")}
                disabled={forgotPasswordLoading}
              />
            </Box>
            <DialogActions sx={{ px: 4, pb: 3 }}>
              <Button
                onClick={handleForgotPasswordDialogClose}
                disabled={forgotPasswordLoading}
                sx={{ '&:hover': { bgcolor: 'error.main', color: 'white' } }}
              >
                {t("cancel")}
              </Button>
              <Button
                onClick={handleForgotPasswordSubmit}
                disabled={forgotPasswordLoading || !forgotPasswordEmail}
              >
                {t("send")}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Product Selection Popup */}
          <ProductSelectionPopup
            open={showProductPopup}
            onClose={() => {}} // Don't allow closing
            onConfirm={handleProductSelection}
            currentProduct={authResponseForPopup?.auth?.current_product}
            subscribedProducts={authResponseForPopup?.subscribed_products || []}
            loading={productChangeLoading}
          />
        </Grid>
      </Container>
    </AuthPageWrapper>
  );
}
